// src/lib/bookmarks.js
// Per-student "saved" content: whole MCQ tests and study papers.
// Doc id = the content's id, so saves are idempotent and easy to look up.
import {
  doc, setDoc, deleteDoc, getDoc, getDocs, collection, query, orderBy, serverTimestamp,
} from "firebase/firestore";
import { db } from "../components/firebase/firebase";

const testRef = (uid, mcqId) => doc(db, "students", uid, "savedTests", mcqId);
const paperRef = (uid, resourceId) => doc(db, "students", uid, "savedPapers", resourceId);

// ── Saved tests ──────────────────────────────────────────────────────────────
export async function isTestSaved(uid, mcqId) {
  if (!uid || !mcqId) return false;
  const snap = await getDoc(testRef(uid, mcqId));
  return snap.exists();
}

// Returns the new saved state (true = now saved, false = now removed).
export async function toggleSavedTest(uid, mcq) {
  if (!uid || !mcq?.id) return false;
  const ref = testRef(uid, mcq.id);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await deleteDoc(ref);
    return false;
  }
  await setDoc(ref, {
    mcqId: mcq.id,
    topic: mcq.topic || "",
    board: mcq.board || null,
    grade: mcq.grade || null,
    subject: mcq.subject || null,
    refId: mcq.refId || null,
    questionCount: mcq.questionCount || null,
    savedAt: serverTimestamp(),
  });
  return true;
}

export async function listSavedTests(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(collection(db, "students", uid, "savedTests"), orderBy("savedAt", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── Saved papers ─────────────────────────────────────────────────────────────
export async function isPaperSaved(uid, resourceId) {
  if (!uid || !resourceId) return false;
  const snap = await getDoc(paperRef(uid, resourceId));
  return snap.exists();
}

export async function toggleSavedPaper(uid, resource) {
  if (!uid || !resource?.id) return false;
  const ref = paperRef(uid, resource.id);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await deleteDoc(ref);
    return false;
  }
  await setDoc(ref, {
    resourceId: resource.id,
    title: resource.title || "",
    type: resource.type || null,
    grade: resource.grade || null,
    subject: resource.subject || null,
    refId: resource.refId || null,
    savedAt: serverTimestamp(),
  });
  return true;
}

export async function listSavedPapers(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(collection(db, "students", uid, "savedPapers"), orderBy("savedAt", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function removeSaved(uid, kind, id) {
  if (!uid || !id) return;
  await deleteDoc(kind === "paper" ? paperRef(uid, id) : testRef(uid, id));
}
